/**
 * Sticky category navigation slider
 */
import { useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'

interface Category {
  id: string
  name: string
}

interface CategoryNavProps {
  categories: Category[]
  activeCategory: string | null
  onCategoryClick: (categoryName: string) => void
}

export function CategoryNav({ categories, activeCategory, onCategoryClick }: CategoryNavProps) {
  const activeBtnRef = useRef<HTMLButtonElement>(null)
  const { scrollRef, canScrollLeft, canScrollRight, scrollBy } = useHorizontalScroll({
    scrollAmount: 300,
    deps: [categories],
  })

  // Scroll active category pill into view when it changes
  useEffect(() => {
    const btn = activeBtnRef.current
    const container = scrollRef.current
    if (!btn || !container) return

    const btnLeft = btn.offsetLeft
    const btnRight = btnLeft + btn.offsetWidth
    const containerLeft = container.scrollLeft
    const containerRight = containerLeft + container.clientWidth

    if (btnLeft < containerLeft) {
      container.scrollTo({ left: btnLeft - 16, behavior: 'smooth' })
    } else if (btnRight > containerRight) {
      container.scrollTo({ left: btnRight - container.clientWidth + 16, behavior: 'smooth' })
    }
  }, [activeCategory, scrollRef])

  if (!categories.length) return null

  return (
    <div className="sticky top-0 z-30 bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur-xl border-b border-border/40 mb-6 sm:mb-8">
      <div className="relative flex items-center">
        {canScrollLeft && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 z-10 h-full rounded-none bg-gradient-to-r from-background via-background/90 to-transparent px-2"
            onClick={() => scrollBy('left')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-hide py-3 px-3 sm:px-4 gap-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => (
            <Button
              key={cat.id}
              ref={activeCategory === cat.name ? activeBtnRef : null}
              variant={activeCategory === cat.name ? 'default' : 'secondary'}
              className={`px-4 sm:px-5 py-2.5 h-auto rounded-full text-sm whitespace-nowrap ${
                activeCategory === cat.name
                  ? 'font-semibold'
                  : 'bg-muted text-foreground font-medium hover:bg-muted/80 active:scale-95'
              }`}
              onClick={() => onCategoryClick(cat.name)}
              aria-current={activeCategory === cat.name ? 'true' : undefined}
            >
              {cat.name}
            </Button>
          ))}
        </div>
        {canScrollRight && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 z-10 h-full rounded-none bg-gradient-to-l from-background via-background/90 to-transparent px-2"
            onClick={() => scrollBy('right')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
